import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http, formatApiError } from "../lib/api";

const LABEL = { agents: "agents", sites: "sites", clients: "clients" };

export const ImportButton = ({ kind, onDone }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`import-${kind}-btn`} className="inline-flex items-center gap-2 border border-slate-300 bg-white hover:bg-slate-50 px-4 py-2 rounded-md text-sm font-semibold"><FileSpreadsheet className="h-4 w-4" /> Importer</button>
      {open && <ImportDialog kind={kind} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onDone(); }} />}
    </>
  );
};

const ImportDialog = ({ kind, onClose, onDone }) => {
  const [prev, setPrev] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const template = async () => {
    const r = await http.get(`/import/${kind}/template`, { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `modele-import-${kind}.xlsx`; a.click();
  };
  const pick = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { setPrev((await http.post(`/import/${kind}/preview`, fd)).data); } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  const ok = prev ? prev.rows.filter((r) => !r.errors.length) : [];
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await http.post(`/import/${kind}/commit`, { rows: ok.map((r) => r.data) });
      toast.success(`${data.created} ${LABEL[kind]} importé(s)${data.errors.length ? ` · ${data.errors.length} ligne(s) refusée(s)` : ""}`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid={`import-${kind}-dialog`}>
        <DialogHeader><DialogTitle>Importer des {LABEL[kind]} depuis Excel ou CSV</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Exportez la liste depuis votre ancien logiciel (Excel .xlsx ou CSV), ou partez du modèle. Les colonnes sont reconnues automatiquement ; vous vérifiez l'aperçu avant l'enregistrement.{kind === "sites" && " La colonne « Client » (nom ou SIRET) rattache chaque site à un client existant : importez d'abord vos clients."}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          <button type="button" onClick={template} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 rounded-md text-sm font-semibold" data-testid={`import-${kind}-template`}><Download className="h-4 w-4" /> Télécharger le modèle</button>
          <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-2 rounded-md text-sm font-semibold disabled:opacity-50" data-testid={`import-${kind}-choose`}><Upload className="h-4 w-4" /> {busy ? "Lecture…" : "Choisir le fichier"}</button>
          <input ref={ref} type="file" accept=".xlsx,.csv,.xlsm" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`import-${kind}-file`} />
        </div>
        {prev && (
          <div className="mt-4" data-testid={`import-${kind}-preview`}>
            <p className="text-sm"><b className="text-emerald-700">{ok.length} ligne(s) prête(s)</b> · <b className="text-rose-700">{prev.rows.length - ok.length} refusée(s)</b>{prev.ignored.length > 0 && <span className="text-slate-500"> · colonnes ignorées : {prev.ignored.join(", ")}</span>}</p>
            <div className="mt-2 border border-slate-200 rounded-md overflow-auto max-h-[45vh]">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0"><tr><th className="p-2 text-left">#</th>{prev.columns.map((c) => <th key={c.key} className="p-2 text-left">{c.label}</th>)}<th className="p-2 text-left">Statut</th></tr></thead>
                <tbody>{prev.rows.map((r, i) => (
                  <tr key={i} className={r.errors.length ? "bg-rose-50" : ""} data-testid={`import-row-${i}`}>
                    <td className="p-2 text-slate-400">{i + 1}</td>{prev.columns.map((c) => <td key={c.key} className="p-2">{c.key.endsWith("_date") && r.data[c.key] ? r.data[c.key].split("-").reverse().join("/") : r.data[c.key] || ""}</td>)}
                    <td className="p-2">{r.errors.length ? <span className="text-rose-700">{r.errors.join(" · ")}</span> : <span className="text-emerald-700 font-semibold">OK</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3"><button type="button" onClick={commit} disabled={busy || !ok.length} className="px-4 py-2 rounded-md bg-[#0B2A7A] text-white text-sm font-semibold disabled:opacity-40" data-testid={`import-${kind}-commit`}>Importer {ok.length} {ok.length > 1 ? LABEL[kind] : LABEL[kind].slice(0, -1)}</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
